/**
 * PastGameForm - Form for adding completed games to the schedule
 * Used in BroadcastsTab to add games that have already been played
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { Archive, X, Wand2 } from "lucide-react";
import { Button } from "../../ui/button";
import { ImageUpload } from "../ImageUpload";
import { extractDominantColor } from "../../../utils/colorExtractor";
import { useSports } from "../../../hooks/useSports";

export const PastGameForm = ({
  pastGameData,
  setPastGameData,
  teams = [],
  events = [],
  onSubmit,
  onCancel,
  submitting = false
}) => {
  const { sports: SPORTS_LIST } = useSports(true);
  const [extractingHomeColor, setExtractingHomeColor] = useState(false);
  const [extractingAwayColor, setExtractingAwayColor] = useState(false);

  const handleExtractColor = async (team, setTeam, setExtracting) => {
    if (!team.logo_url) return;
    setExtracting(true);
    try {
      const color = await extractDominantColor(team.logo_url);
      if (color) {
        setTeam({ ...team, color });
      }
    } catch (err) {
      console.error("Color extraction failed:", err);
    } finally {
      setExtracting(false);
    }
  };

  const handleTeamSelect = (teamId, teamType) => {
    if (!teamId) return;
    const selectedTeam = teams.find(t => t.id === teamId);
    if (selectedTeam) {
      const teamData = {
        name: selectedTeam.name,
        abbreviation: selectedTeam.abbreviation || "",
        mascot: selectedTeam.mascot || "",
        color: selectedTeam.primary_color || "#FFFFFF",
        logo_url: selectedTeam.logo_url || ""
      };
      
      if (teamType === 'home') {
        setPastGameData({ ...pastGameData, home_team: teamData });
      } else {
        setPastGameData({ ...pastGameData, away_team: teamData });
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="p-6 bg-zinc-900/50 border border-orange-500/30 space-y-6"
      data-testid="past-game-form"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Archive className="w-5 h-5 text-orange-500" />
          <div>
            <h3 className="font-secondary text-lg font-bold uppercase text-white">Add Past Game</h3>
            <p className="text-xs text-zinc-500">Add a completed game result to the schedule</p>
          </div>
        </div>
        <button 
          type="button" 
          onClick={onCancel}
          className="text-zinc-500 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Basic Info Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Sport *</label>
          <select 
            value={pastGameData.sport} 
            onChange={(e) => setPastGameData({ ...pastGameData, sport: e.target.value })} 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          >
            {SPORTS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Game Date *</label>
          <input 
            type="date" 
            value={pastGameData.game_date} 
            onChange={(e) => setPastGameData({ ...pastGameData, game_date: e.target.value })} 
            required 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Event (Optional)</label>
          <select 
            value={pastGameData.event_id} 
            onChange={(e) => setPastGameData({ ...pastGameData, event_id: e.target.value })} 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          >
            <option value="">-- No Event --</option>
            {events.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      </div>
      
      {/* Teams Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Home Team */}
        <TeamInput
          label="Home Team *"
          team={pastGameData.home_team}
          setTeam={(t) => setPastGameData({ ...pastGameData, home_team: t })}
          teams={teams}
          onTeamSelect={(id) => handleTeamSelect(id, 'home')}
          extractingColor={extractingHomeColor}
          onExtractColor={() => handleExtractColor(
            pastGameData.home_team, 
            (t) => setPastGameData({ ...pastGameData, home_team: t }),
            setExtractingHomeColor
          )}
        />
        
        {/* Away Team */}
        <TeamInput
          label="Away Team *"
          team={pastGameData.away_team}
          setTeam={(t) => setPastGameData({ ...pastGameData, away_team: t })}
          teams={teams}
          onTeamSelect={(id) => handleTeamSelect(id, 'away')}
          extractingColor={extractingAwayColor}
          onExtractColor={() => handleExtractColor(
            pastGameData.away_team, 
            (t) => setPastGameData({ ...pastGameData, away_team: t }),
            setExtractingAwayColor
          )}
        />
      </div>
      
      {/* Final Score */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
            {pastGameData.home_team?.name || 'Home'} Final Score *
          </label>
          <input 
            type="number" 
            value={pastGameData.home_score} 
            onChange={(e) => setPastGameData({ ...pastGameData, home_score: e.target.value })} 
            min="0"
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
            {pastGameData.away_team?.name || 'Away'} Final Score *
          </label>
          <input 
            type="number" 
            value={pastGameData.away_score} 
            onChange={(e) => setPastGameData({ ...pastGameData, away_score: e.target.value })} 
            min="0"
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
      </div>
      
      {/* Video & Stats Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Watch URL (YouTube/Video)</label>
          <input 
            type="url" 
            value={pastGameData.watch_url || ""} 
            onChange={(e) => setPastGameData({ ...pastGameData, watch_url: e.target.value })} 
            placeholder="https://youtube.com/watch?v=..."
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Box Score / Stats URL</label>
          <input 
            type="url" 
            value={pastGameData.box_score_url || ""} 
            onChange={(e) => setPastGameData({ ...pastGameData, box_score_url: e.target.value })} 
            placeholder="https://statmoose.com/..."
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
      </div>
      
      {/* Submit Button */}
      <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
        <Button
          type="button"
          onClick={onCancel}
          className="bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs px-4 py-2 h-auto rounded-none"
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting || !pastGameData.sport || !pastGameData.game_date || !pastGameData.home_team?.name || !pastGameData.away_team?.name}
          className="bg-orange-500 hover:bg-orange-600 text-white font-secondary text-xs px-6 py-2 h-auto rounded-none disabled:opacity-50"
        >
          {submitting ? "Adding..." : "Add Past Game"}
        </Button>
      </div>
    </motion.div>
  );
};

// TeamInput component for reuse
const TeamInput = ({
  label,
  team,
  setTeam,
  teams,
  onTeamSelect,
  extractingColor,
  onExtractColor
}) => {
  return (
    <div className="p-4 bg-zinc-800/50 border border-white/5">
      <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400 mb-3">{label}</h4>
      <div className="space-y-3">
        {/* Team Selector */}
        <div className="mb-3">
          <label className="block text-[10px] text-zinc-500 mb-1">Select Team</label>
          <select 
            onChange={(e) => onTeamSelect(e.target.value)}
            className="w-full bg-zinc-900 border border-orange-500/30 text-white px-3 py-2 text-sm"
          >
            <option value="">-- Quick Select Team --</option>
            {teams.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} {t.mascot ? `(${t.mascot})` : ''} - {t.sport}
              </option>
            ))}
          </select>
        </div>
        
        {/* Team Name */}
        <input 
          type="text" 
          value={team.name || ""} 
          onChange={(e) => setTeam({ ...team, name: e.target.value })} 
          placeholder="Team Name" 
          className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
        />
        
        {/* Abbreviation & Mascot */}
        <div className="grid grid-cols-2 gap-2">
          <input 
            type="text" 
            value={team.abbreviation || ""} 
            onChange={(e) => setTeam({ ...team, abbreviation: e.target.value })} 
            placeholder="Abbrev (e.g., NYG)" 
            className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
          />
          <input 
            type="text" 
            value={team.mascot || ""} 
            onChange={(e) => setTeam({ ...team, mascot: e.target.value })} 
            placeholder="Mascot" 
            className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        
        {/* Color Picker */}
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-zinc-500">Color:</label>
          <input 
            type="color" 
            value={team.color || "#FFFFFF"} 
            onChange={(e) => setTeam({ ...team, color: e.target.value })} 
            className="w-10 h-8 border-0 bg-transparent cursor-pointer" 
          />
          <span className="text-xs text-zinc-600">{team.color}</span>
          {team.logo_url && onExtractColor && (
            <button 
              type="button" 
              onClick={onExtractColor}
              disabled={extractingColor}
              className="ml-auto text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1 disabled:opacity-50"
            >
              <Wand2 className="w-3 h-3" />
              {extractingColor ? "..." : "Auto"}
            </button>
          )}
        </div>
        
        {/* Logo Upload */}
        <ImageUpload
          value={team.logo_url || ""}
          onChange={(url) => setTeam({ ...team, logo_url: url })}
          label="Team Logo"
          type="logo"
        />
      </div>
    </div>
  );
};

export default PastGameForm;
